/**
 * Depth-grid cell encoding (spec 09). One uint8 per cell:
 *   0        = land / blocked
 *   255      = unknown (treated as blocked)
 *   1..254   = min charted depth in 0.25 m units, capped at 63 m
 *
 * Charted depths reference sjøkartnull (≈ LAT), so routing on them is
 * conservative w.r.t. tide by construction — no tide model needed.
 */

export const CELL_LAND = 0;
export const CELL_UNKNOWN = 255;
export const DEPTH_UNIT_M = 0.25;
export const MAX_DEPTH_M = 63;
const MAX_DEPTH_CELL = Math.round(MAX_DEPTH_M / DEPTH_UNIT_M); // 252

/** Rasterizer side: min charted depth (m) → cell. ≤0 dries → blocked. */
export function depthToCell(depthM: number): number {
  if (!Number.isFinite(depthM) || depthM <= 0) return CELL_LAND;
  const units = Math.round(depthM / DEPTH_UNIT_M);
  return Math.min(MAX_DEPTH_CELL, Math.max(1, units));
}

/** Cell → charted depth (m), or null for land/unknown. */
export function cellToDepthM(cell: number): number | null {
  if (cell === CELL_LAND || cell === CELL_UNKNOWN) return null;
  return cell * DEPTH_UNIT_M;
}

/** A cell is enterable iff its charted depth ≥ draught + clearance (spec 09). */
export function isPassable(cell: number, requiredDepthM: number): boolean {
  if (cell === CELL_LAND || cell === CELL_UNKNOWN) return false;
  return cell * DEPTH_UNIT_M >= requiredDepthM;
}

/** Aggregate 4 child cells into a coarser parent — conservative (the min). */
export function aggregateCells(a: number, b: number, c: number, d: number): number {
  // Blocked (land/unknown) dominates; otherwise the shallowest depth wins.
  const vals = [a, b, c, d];
  if (vals.some((v) => v === CELL_LAND)) return CELL_LAND;
  const depths = vals.filter((v) => v !== CELL_UNKNOWN);
  if (depths.length === 0) return CELL_UNKNOWN;
  return Math.min(...depths);
}
